import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState(null); // 'sending' | 'success' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus('sending');
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
      setTimeout(() => setStatus(null), 4000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus(null), 4000);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ background: '#050505' }}
    >
      <div className="max-w-2xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-heading text-4xl sm:text-5xl md:text-6xl text-white font-bold tracking-tighter mb-4"
          data-testid="contact-label"
        >
          {t.contact.label}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-body text-zinc-400 mb-12 text-base md:text-lg"
        >
          {t.contact.subtitle}
        </motion.p>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="space-y-6"
        >
          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-300 font-body text-sm">
              {t.contact.name}
            </Label>
            <Input
              id="name"
              data-testid="contact-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t.contact.namePlaceholder}
              required
              className="bg-[#111111] border-white/10 rounded-xl text-white placeholder:text-zinc-600 focus-visible:ring-[#e14141] focus-visible:border-[#e14141] h-12 font-body"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-zinc-300 font-body text-sm">
              {t.contact.email}
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="contact-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t.contact.emailPlaceholder}
              required
              className="bg-[#111111] border-white/10 rounded-xl text-white placeholder:text-zinc-600 focus-visible:ring-[#e14141] focus-visible:border-[#e14141] h-12 font-body"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-zinc-300 font-body text-sm">
              {t.contact.message}
            </Label>
            <Textarea
              id="message"
              data-testid="contact-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t.contact.messagePlaceholder}
              required
              rows={5}
              className="bg-[#111111] border-white/10 rounded-xl text-white placeholder:text-zinc-600 focus-visible:ring-[#e14141] focus-visible:border-[#e14141] font-body resize-none"
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={status === 'sending'}
            className="w-full bg-[#e14141] text-white font-body text-sm py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-[#d33333] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01]"
          >
            {status === 'sending' ? (
              <>{t.contact.sending}</>
            ) : (
              <>
                {t.contact.send}
                <Send size={16} />
              </>
            )}
          </button>

          {/* Status Messages */}
          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="contact-success-message"
              className="flex items-center gap-2 text-green-400 font-body text-sm"
            >
              <CheckCircle size={16} />
              {t.contact.success}
            </motion.div>
          )}
          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="contact-error-message"
              className="flex items-center gap-2 text-[#e14141] font-body text-sm"
            >
              <AlertCircle size={16} />
              {t.contact.error}
            </motion.div>
          )}
        </motion.form>
      </div>
    </section>
  );
}
